import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u?.role === "admin" ? "/admin" : (location.state?.from || "/app"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative z-10 flex flex-col">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/"><Logo /></Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm">
          <p className="text-xs font-medium tracking-overline text-primary uppercase mb-3">Welcome back</p>
          <h1 className="text-3xl font-bold tracking-tight mb-8">Sign in to Haki</h1>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-secondary border border-border rounded-xl px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="you@campus.ac.za"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-secondary border border-border rounded-xl px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-primary text-white rounded-full font-medium py-3 inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-60"
            >
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            New to Haki?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-primary font-medium">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
